import moment from 'moment';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { captureException } from '@/utils/sentry';
import { createStore, StoreApi, useStore } from 'zustand';
import { EmployeeRestaurant, useUserContext } from './user-context';
import { NetworkStatus, useNetworkStatus } from './network-status-context';
import {
  OfflineDataStatus,
  OfflineDataStatusAdapter,
} from '@/adapters/offline-mode';
import { OfflineSyncType } from '@/utils/offline-mode';
import { Platform } from 'react-native';
import { useComponentFocus } from '@/hooks/use-component-focus';
import { useDebouncedCallback } from 'use-debounce';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useNetworkGracePeriod } from '@/hooks/use-network-grace-period';
import { useOfflineDownload } from '@/hooks/use-offline-download';
import { useShallow } from 'zustand/shallow';

export const MAX_CONCURRENT_ACCOUNT_DOWNLOADS = 5;
const OFFLINE_SYNC_DEBOUNCE_DELAY_MS = 1000;

type NonAutoSyncedTypes = OfflineSyncType.MinimalVariantPack;

const OFFLINE_SYNC_HOUR_INTERVAL_MAP: Record<
  Exclude<OfflineSyncType, NonAutoSyncedTypes>,
  number
> = {
  [OfflineSyncType.BatchTagPrinting]: 24,
  [OfflineSyncType.CreditRequest]: 24,
  [OfflineSyncType.OrderConstraints]: 24,
  [OfflineSyncType.OrderContext]: 24,
  [OfflineSyncType.OrderGuide]: 24,
  [OfflineSyncType.PriceEditDisplayInfo]: 24,
  [OfflineSyncType.UserContext]: 24,
  [OfflineSyncType.VariantPackInfo]: 24,
  [OfflineSyncType.VisibleVariantPacks]: 24,
};

export interface OfflineDownloadProgress {
  [restaurantUUID: string]: boolean | null;
}

const getHoursSinceLastSync = (currentDate: Date, lastSyncDate: Date) => {
  return moment(currentDate).diff(moment(lastSyncDate), 'hours');
};

export const getExpiredSyncTypesToUpdate = (
  offlineDataStatus: OfflineDataStatus | undefined,
): OfflineSyncType[] => {
  // Download all sync types if no data status exists or if using the new data status format
  if (!offlineDataStatus || 'downloadedDate' in offlineDataStatus) {
    return Object.values(OfflineSyncType);
  }

  const currentTime = new Date();
  const syncTypesToUpdate: OfflineSyncType[] = [];

  for (const [type, hoursSyncInterval] of Object.entries(
    OFFLINE_SYNC_HOUR_INTERVAL_MAP,
  )) {
    const syncType = type as OfflineSyncType;
    const previousSyncDate = offlineDataStatus[syncType]?.downloadedDate;

    // If no previous sync date exists, this sync type needs to be downloaded
    if (!previousSyncDate) {
      syncTypesToUpdate.push(syncType);
      continue;
    }

    const hoursSinceLastSync = getHoursSinceLastSync(
      currentTime,
      new Date(previousSyncDate),
    );

    if (hoursSinceLastSync >= hoursSyncInterval) {
      syncTypesToUpdate.push(syncType);
    }
  }

  return syncTypesToUpdate;
};

export interface OfflineDownloadManagementContextType {
  abortController: AbortController | null;
  cancelDownload: () => void;
  downloadProgress: OfflineDownloadProgress | null;
  downloadSelectedRestaurants: (
    onChunkDownloadUpdate?: () => Promise<void>,
  ) => Promise<void>;
  removeSelectedRestaurants: () => Promise<void>;
  resetDownloadProgress: () => void;
  resetSelection: () => void;
  selectAllRestaurants: (restaurantRelationships: EmployeeRestaurant[]) => void;
  selectedRestaurantRelationships: EmployeeRestaurant[];
  toggleRestaurantSelection: (relationship: EmployeeRestaurant) => void;
}

export const OfflineDownloadManagementContext =
  createContext<StoreApi<OfflineDownloadManagementContextType> | null>(null);

interface Props {
  children?: React.ReactNode;
}

export const OfflineDownloadManagementContextProvider: React.FC<Props> = ({
  children,
}) => {
  const offlineModeConfig = useDynamicConfigValue('offline_mode_config');
  const { restaurantRelationships } = useUserContext();
  const { networkStatus } = useNetworkStatus();
  const { setGraceTimeout } = useNetworkGracePeriod();
  const {
    downloadEligibleOfflineData,
    getEnabledSyncTypes,
    removeOfflineData,
  } = useOfflineDownload();
  // Used to prevent automatic syncs after the user opts to cancel sync
  const cancelledDownloadRef = useRef(false);

  const [store] = useState(() => {
    return createStore<OfflineDownloadManagementContextType>((set, get) => ({
      abortController: null,
      cancelDownload: () => {
        cancelledDownloadRef.current = true;
        get().abortController?.abort();
        set({ abortController: null, downloadProgress: null });
      },
      downloadProgress: null,
      downloadSelectedRestaurants: async (onChunkDownloadUpdate) => {
        const selectedRestaurantRelationships =
          get().selectedRestaurantRelationships;
        const abortController = new AbortController();
        const signal = abortController.signal;
        const dataStatusAdapter = new OfflineDataStatusAdapter();
        set({ abortController });

        // Initialize progress with nulls
        const initialProgress: OfflineDownloadProgress = Object.fromEntries(
          selectedRestaurantRelationships.map((r) => [r.uuid, null]),
        );
        set({ downloadProgress: initialProgress });

        const updateProgress = (
          restaurantUUID: string,
          isDownloaded: boolean,
        ) => {
          if (abortController.signal.aborted) {
            return;
          }

          set((state) => ({
            downloadProgress: {
              ...(state.downloadProgress ?? {}),
              [restaurantUUID]: isDownloaded,
            },
          }));
        };

        const downloadRestaurant = async (
          relationship: EmployeeRestaurant,
        ): Promise<[string, OfflineDataStatus | null]> => {
          const restaurantUUID = relationship.uuid;

          try {
            const enabledSyncTypes = getEnabledSyncTypes(relationship);

            // Mark as downloaded if no sync types to update
            if (enabledSyncTypes.length === 0) {
              updateProgress(restaurantUUID, true);
              return [restaurantUUID, null];
            }

            const updatedRestaurantStatus = await downloadEligibleOfflineData(
              relationship,
              relationship.supplierUUID,
            );

            updateProgress(restaurantUUID, Boolean(updatedRestaurantStatus));
            return [restaurantUUID, updatedRestaurantStatus];
          } catch (e) {
            updateProgress(restaurantUUID, false);
          }

          return [restaurantUUID, null];
        };

        let i = 0;
        while (i < selectedRestaurantRelationships.length && !signal.aborted) {
          const chunk = selectedRestaurantRelationships.slice(
            i,
            i + MAX_CONCURRENT_ACCOUNT_DOWNLOADS,
          );

          const results = await Promise.allSettled(
            chunk.map(downloadRestaurant),
          );
          const currentDataStatus = (await dataStatusAdapter.fetch()) ?? {};

          // Update data status per chunk after settling results
          const updatedDataStatus = results.reduce(
            (acc, result) => {
              if (
                result.status === 'fulfilled' &&
                result.value &&
                Array.isArray(result.value)
              ) {
                const [restaurantUUID, dataStatus] = result.value;
                if (restaurantUUID && dataStatus) {
                  acc[restaurantUUID] = {
                    ...(currentDataStatus?.[restaurantUUID] ?? {}),
                    ...dataStatus,
                  };
                }
              }
              return acc;
            },
            { ...currentDataStatus },
          );

          await dataStatusAdapter.save(updatedDataStatus);
          i += MAX_CONCURRENT_ACCOUNT_DOWNLOADS;
          await onChunkDownloadUpdate?.();
        }

        // Reset the abort controller and selections after completed download
        // and auto-dismiss download header after 5 seconds
        if (abortController) {
          set({ abortController: null, selectedRestaurantRelationships: [] });
        }
        setTimeout(() => set({ downloadProgress: null }), 5000);
      },
      removeSelectedRestaurants: async () => {
        const selectedRestaurantRelationships =
          get().selectedRestaurantRelationships;
        try {
          for (const relationship of selectedRestaurantRelationships) {
            await removeOfflineData(
              relationship.uuid,
              relationship.supplierUUID,
            );
          }
        } catch (e) {
          captureException(e);
        }

        set({ selectedRestaurantRelationships: [] });
      },
      resetDownloadProgress: () => set({ downloadProgress: null }),
      resetSelection: () => set({ selectedRestaurantRelationships: [] }),
      selectAllRestaurants: (restaurantRelationships) => {
        if (restaurantRelationships.length === 0) {
          return;
        }

        set((state) => {
          const prev = state.selectedRestaurantRelationships;
          const prevUUIDs = new Set(prev.map((r) => r.uuid));
          const allSelected = restaurantRelationships.every((r) =>
            prevUUIDs.has(r.uuid),
          );

          if (allSelected) {
            const selectedUUIDs = new Set(
              restaurantRelationships.map((r) => r.uuid),
            );
            const newRelationships = prev.filter(
              (r) => !selectedUUIDs.has(r.uuid),
            );
            return { selectedRestaurantRelationships: newRelationships };
          }

          const newRelationships = [
            ...prev,
            ...restaurantRelationships.filter((r) => !prevUUIDs.has(r.uuid)),
          ];
          return { selectedRestaurantRelationships: newRelationships };
        });
      },
      selectedRestaurantRelationships: [],
      toggleRestaurantSelection: (relationship) => {
        set((state) => {
          const isSelected = state.selectedRestaurantRelationships.some(
            (r) => r.uuid === relationship.uuid,
          );
          const updatedRelationships = isSelected
            ? state.selectedRestaurantRelationships.filter(
                (r) => r.uuid !== relationship.uuid,
              )
            : [...state.selectedRestaurantRelationships, relationship];

          return { selectedRestaurantRelationships: updatedRelationships };
        });
      },
    }));
  });

  const syncOfflineRestaurants = useCallback(async () => {
    const dataStatusAdapter = new OfflineDataStatusAdapter();
    const dataStatus = await dataStatusAdapter.fetch();

    if (!dataStatus || !offlineModeConfig?.foreground_sync_enabled) {
      return;
    }

    const restaurantsToDownload = Object.keys(dataStatus).reduce<
      EmployeeRestaurant[]
    >((acc, restaurantUUID) => {
      const relationship = restaurantRelationships.find(
        (r) => r.uuid === restaurantUUID,
      );

      if (!relationship) {
        return acc;
      }

      const enabledSyncTypes = getEnabledSyncTypes(relationship);
      const enabledExpiredSyncTypesToUpdate = getExpiredSyncTypesToUpdate(
        dataStatus[restaurantUUID],
      ).filter((syncType) => enabledSyncTypes.includes(syncType));

      if (enabledExpiredSyncTypesToUpdate.length > 0) {
        acc.push(relationship);
      }

      return acc;
    }, []);

    // Skip automatic sync if there are no restaurants to download
    // or if user has manually selected restaurants for download
    if (
      restaurantsToDownload.length === 0 ||
      store.getState().selectedRestaurantRelationships.length > 0
    ) {
      return;
    }

    store.setState({ selectedRestaurantRelationships: restaurantsToDownload });
    store.getState().downloadSelectedRestaurants();
  }, [getEnabledSyncTypes, offlineModeConfig, restaurantRelationships, store]);

  const debouncedSyncOfflineRestaurants = useDebouncedCallback(async () => {
    await syncOfflineRestaurants();
  }, OFFLINE_SYNC_DEBOUNCE_DELAY_MS);

  useComponentFocus({
    onFocus: useCallback(() => {
      if (networkStatus === NetworkStatus.CONNECTED) {
        setGraceTimeout(async () => {
          if (Platform.OS !== 'web' && !cancelledDownloadRef.current) {
            await debouncedSyncOfflineRestaurants();
          }
        });
      }
    }, [debouncedSyncOfflineRestaurants, networkStatus, setGraceTimeout]),
  });

  useEffect(() => {
    return () => {
      store.getState().abortController?.abort();
    };
  }, [store]);

  return (
    <OfflineDownloadManagementContext.Provider value={store}>
      {children}
    </OfflineDownloadManagementContext.Provider>
  );
};

export const useOfflineDownloadManagementContext = <T,>(
  selector: (s: OfflineDownloadManagementContextType) => T,
): T => {
  const store = useContext(OfflineDownloadManagementContext);
  if (!store) {
    throw new Error(
      'useOfflineDownloadManagement must be used within an OfflineDownloadManagementContextProvider',
    );
  }

  return useStore(store, useShallow(selector));
};

export const useSelectedOfflineRestaurantUUIDs = () => {
  const selectedRestaurants = useOfflineDownloadManagementContext(
    (s) => s.selectedRestaurantRelationships,
  );

  return useMemo(
    () => new Set(selectedRestaurants.map((r) => r.uuid)),
    [selectedRestaurants],
  );
};

export const useCurrentDownloadPercentage = () => {
  const downloadProgress = useOfflineDownloadManagementContext(
    (s) => s.downloadProgress,
  );

  return useMemo(() => {
    if (!downloadProgress) {
      return 0;
    }

    const accountsToDownload = Object.keys(downloadProgress).length;
    if (accountsToDownload === 0) {
      return 0;
    }

    const processedAccounts = Object.values(downloadProgress).filter(
      (value) => typeof value === 'boolean',
    ).length;

    // Show 1% when starting download to make the progress bar visible
    return processedAccounts === 0
      ? 1
      : Math.round((processedAccounts / accountsToDownload) * 100);
  }, [downloadProgress]);
};

export const DummyOfflineDownloadManagementContextProvider: React.FC<Props> = ({
  children,
}) => {
  const store = createStore<OfflineDownloadManagementContextType>(() => ({
    abortController: null,
    cancelDownload: () => {},
    downloadProgress: null,
    downloadSelectedRestaurants: async () => {},
    removeSelectedRestaurants: async () => {},
    resetDownloadProgress: () => {},
    resetSelection: () => {},
    selectAllRestaurants: () => {},
    selectedRestaurantRelationships: [],
    toggleRestaurantSelection: () => {},
  }));

  return (
    <OfflineDownloadManagementContext.Provider value={store}>
      {children}
    </OfflineDownloadManagementContext.Provider>
  );
};
